import React, { Component } from "react";
import {
  Alert,
  Col,
  Container,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Row,
  Spinner,
} from "react-bootstrap";
import { ApplicationState, history } from "../../store";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { verifyOtp, resendOtp, backToLogin } from "../../store/auth/actions";
import { APP_NAME } from "../../constants";
import { UserAssignmentRole } from "../../store/auth/types";
import { districtRoles } from "../../store/auth/constants";
import { Link, RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";

type PropsFromState = {
  isAuthenticated: boolean;
  isLoading: boolean;
  isResendingOtp: boolean;
  verifyOtpError?: {
    loginSuccess?: boolean;
    remainingAttempts?: number;
    isOtpExpired?: boolean;
    lockTime?: number;
    isOtpLocked?: boolean
  };
  resendOtpError?: {
    resendSuccess?: boolean;
    isOtpExpired?: boolean;
  };
  userInfo?: any;
  userNameCredential?: string;
};

type MatchParams = {
  success: string;
};

type Props = PropsFromState & {
  verifyOtp: (otp: string, tempToken: string) => any;
  resendOtp: (tempToken: string) => any;
  backToLogin: () => any;
} & RouteComponentProps<MatchParams>;

type State = {
  otp: string;
  hasOtpChanged: boolean;
  resendDisabled: boolean;
  resendTimer: number;
};

const RESEND_OTP_INTERVAL = 30; // 30 seconds

class VerifyOTPPage extends Component<Props, State> {
  private intervalId: NodeJS.Timeout | null = null;
  private hasRedirected: boolean = false;

  state: Readonly<State> = {
    otp: "",
    hasOtpChanged: false,
    resendDisabled: true,
    resendTimer: RESEND_OTP_INTERVAL,
  };

  componentDidMount(): void {
    // Check if tempToken exists
    const tempToken = sessionStorage.getItem('tempToken');
    if (!tempToken) {
      // Clear any existing OTP errors and redirect to login
      this.backToLogin();
      return;
    }

    // Start resend timer
    this.startResendTimer();
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    // Check if tempToken still exists on every update
    const tempToken = sessionStorage.getItem('tempToken');
    if (!tempToken && !this.hasRedirected) {
      this.backToLogin();
      return;
    }

    // If authentication is successful, redirect to appropriate dashboard
    // Check both: authentication changed AND userInfo is loaded
    if (
      !prevProps.isAuthenticated && 
      this.props.isAuthenticated && 
      this.props.userInfo &&
      !this.hasRedirected
    ) {
      const role = this.props.userInfo?.profile.current_assignment?.role;
      const licenseStatus = this.props.userInfo?.current_license?.status;
      
      let pathname = "/data";

      if (role && districtRoles.includes(role)) {
        pathname = "/district";
      }

      if (licenseStatus !== "active" && role !== UserAssignmentRole.SUPERUSER) {
        pathname = "/not-active-license";
      }

      if (UserAssignmentRole.SUPERUSER === role) {
        pathname = "/superuser/districts";
      }

      // Mark as redirected to prevent duplicate redirects
      this.hasRedirected = true;
      history.push(pathname);
    }

    // Alternative: Also check if userInfo just loaded while already authenticated
    if (
      this.props.isAuthenticated &&
      !prevProps.userInfo &&
      this.props.userInfo &&
      !this.hasRedirected
    ) {
      const role = this.props.userInfo?.profile.current_assignment?.role;
      const licenseStatus = this.props.userInfo?.current_license?.status;
      
      let pathname = "/data";

      if (role && districtRoles.includes(role)) {
        pathname = "/district";
      }

      if (licenseStatus !== "active" && role !== UserAssignmentRole.SUPERUSER) {
        pathname = "/not-active-license";
      }

      if (UserAssignmentRole.SUPERUSER === role) {
        pathname = "/superuser/districts";
      }

      // Mark as redirected to prevent duplicate redirects
      this.hasRedirected = true;
      history.push(pathname);
    }

    // Check if remaining attempts exceeded
    if (this.props.verifyOtpError?.remainingAttempts === 0 && !this.hasRedirected) {
      setTimeout(() => {
        this.backToLogin();
      }, 3000);
    }

    // Check if OTP session expired (from resend)
    if (this.props.resendOtpError?.isOtpExpired && !this.hasRedirected) {
      setTimeout(() => {
        this.backToLogin();
      }, 3000);
    }
  }

  componentWillUnmount(): void {
    this.clearTimer();
  }

  backToLogin = () => {
    sessionStorage.removeItem('tempToken');
    this.hasRedirected = true;
    this.props.backToLogin();
    history.push('/login');
  };

  startResendTimer = () => {
    this.intervalId = setInterval(() => {
      this.setState((prevState) => {
        if (prevState.resendTimer <= 1) {
          this.clearTimer();
          return {
            resendTimer: 0,
            resendDisabled: false,
            otp: prevState.otp,
            hasOtpChanged: prevState.hasOtpChanged,
          };
        }
        return {
          resendTimer: prevState.resendTimer - 1,
          resendDisabled: true,
          otp: prevState.otp,
          hasOtpChanged: prevState.hasOtpChanged,
        };
      });
    }, 1000);
  };

  clearTimer = () => {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  };

  handleChange = (event: React.FormEvent<any>) => {
    const { value } = event.target as HTMLInputElement;
    // Only allow numeric input and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    this.setState({
      otp: numericValue,
      hasOtpChanged: true,
    });
  };

  handleSubmit = (event: any) => {
    event.preventDefault();
    const { otp } = this.state;
    const tempToken = sessionStorage.getItem('tempToken');

    if (!tempToken) {
      history.push('/login');
      return;
    }

    if (otp.length === 6) {
      this.props.verifyOtp(otp, tempToken);
      this.setState({ hasOtpChanged: false });
    }
  };

  handleResendOtp = () => {
    const tempToken = sessionStorage.getItem('tempToken');
    if (!tempToken) {
      history.push('/login');
      return;
    }

    this.props.resendOtp(tempToken);
    
    // Reset timer
    this.setState({
      resendDisabled: true,
      resendTimer: RESEND_OTP_INTERVAL,
    });
    this.startResendTimer();
  };

  formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  render() {
    const { isLoading, isResendingOtp, verifyOtpError, resendOtpError, match, userNameCredential } = this.props;
    const { hasOtpChanged, otp, resendDisabled, resendTimer } = this.state;

    // Check if attempts exceeded
    const attemptsExceeded = verifyOtpError?.remainingAttempts === 0;
    return (
      <div>
        <Container className="loginContainer">
          <Row className="align-items-center">
            <Col sm>
              <h1 className="hMax">
                Two-Factor Authentication
              </h1>
              <br />
              <p className="text-white text-center mb-1">
                We've sent a verification code to your email address.
              </p>
              <p className="text-white text-center">
                Please enter the 6-digit code to continue.
              </p>
              <hr />
              <h2 className="text-white text-center">Need help?</h2>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <button 
                  className="blueBtnMd m-2" 
                  onClick={() => window.open("https://readycoach.zendesk.com", "_blank")}
                >
                  Contact Us Today
                </button>
              </div>
            </Col>
            <Col sm>
              <div className="welcomeInputContainer" style={{paddingTop: "10px"}}>
                {/* Back to Login link - Top Left Corner */}
                <div className="mb-3">
                  <Link
                    data-cy="back-to-login"
                    to={"/login"}
                    className="clickableLabel"
                    onClick={this.backToLogin}
                    style={{ fontSize: "14px" }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} className="mr-2" />
                    <span className="d-inline font-weight-semibold">
                      Back
                    </span>
                  </Link>
                </div>

                <div>
                  <h2 className="m-0">
                    <strong>Verify Your Identity</strong>
                  </h2>
                  <h5>Enter the OTP sent to your email</h5>
                </div>
                {match.params.success === "true" && userNameCredential && (
                  <Alert variant="success">
                    An OTP has been sent to {userNameCredential}. Please enter the OTP to complete the login process.
                  </Alert>
                )}
                {attemptsExceeded && verifyOtpError?.isOtpLocked && (
                  <Alert variant="danger">
                    Maximum OTP verification attempts exceeded. Please try again after {verifyOtpError.lockTime! / 60} minutes. Redirecting to login page...
                  </Alert>
                )}


                {verifyOtpError?.isOtpExpired && !attemptsExceeded && (
                  <Alert variant="warning">
                  Your OTP has expired. Please request a new one.
                  </Alert>
                )}

                {resendOtpError?.isOtpExpired && !attemptsExceeded && (
                  <Alert variant="danger">
                  Your OTP session has expired. Redirecting to login page...
                  </Alert>
                )}

                {verifyOtpError && !hasOtpChanged && !attemptsExceeded && !verifyOtpError.isOtpExpired && (
                  <Alert variant="danger">
                    {verifyOtpError.remainingAttempts !== undefined && verifyOtpError.remainingAttempts > 0 && (
                      <span> {!verifyOtpError.loginSuccess ? "Invalid OTP!" : ""} {verifyOtpError.remainingAttempts} attempt(s) remaining.</span>
                    )}
                  </Alert>
                )}

                <Form id="verify-otp-form" onSubmit={this.handleSubmit}>
                  <FormGroup controlId="otp">
                    <FormLabel>
                      <h4>Enter OTP</h4>
                    </FormLabel>
                    <FormControl
                      autoFocus
                      name="otp"
                      type="text"
                      value={otp}
                      onChange={this.handleChange}
                      className="fullWidthInput"
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      required
                      disabled={attemptsExceeded}
                    />
                  </FormGroup>

                  <div className="d-flex justify-content-between align-items-center" style={{ marginTop: "20px" }}>
                    {/* Resend OTP link - Left Side */}
                    <div>
                      {isResendingOtp ? (
                        <span className="text-muted" style={{ fontSize: "14px" }}>
                          <Spinner animation="border" size="sm" style={{ marginRight: "5px" }} />
                          Resending...
                        </span>
                      ) : resendDisabled ? (
                        <span className="text-muted" style={{ fontSize: "14px", cursor: "not-allowed" }}>
                          Resend OTP ({this.formatTime(resendTimer)})
                        </span>
                      ) : (
                        <Link
                          to="#"
                          className="clickableLabel"
                          onClick={(e) => {
                            e.preventDefault();
                            this.handleResendOtp();
                          }}
                          style={{ 
                            fontSize: "14px",
                            pointerEvents: attemptsExceeded ? "none" : "auto",
                            opacity: attemptsExceeded ? 0.5 : 1
                          }}
                        >
                          Resend OTP
                        </Link>
                      )}
                    </div>

                    {/* Verify button - Right Side */}
                    <div>
                      <button
                        className="blueBtnMd"
                        type="submit"
                        disabled={otp.length !== 6 || isLoading || attemptsExceeded}
                      >
                        Verify{" "}
                        {isLoading && <Spinner animation="border" size="sm" />}
                      </button>
                    </div>
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

const mapStateToProps = ({ auth }: ApplicationState): PropsFromState => {
  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading.verifyOtp,
    isResendingOtp: auth.isLoading.resendOtp,
    verifyOtpError: auth.errors.verifyOtp,
    resendOtpError: auth.errors.resendOtp,
    userInfo: auth.userInfo,
    userNameCredential: auth.userNameCredential,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      verifyOtp: verifyOtp,
      resendOtp: resendOtp,
      backToLogin: backToLogin
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(VerifyOTPPage);
